import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Receipt, Package, TrendingUp,
  Truck, Bell, Settings, BookOpen, Menu, X, ChevronRight,
  Zap, LogOut, Users, ExternalLink, DollarSign, Send
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/manifests', label: 'Manifests', icon: FileText, badge: 'Auto-Sync' },
  { path: '/invoices', label: 'Invoices', icon: Receipt },
  { path: '/kpl-mix', label: 'KPL Mix', icon: Package },
  { path: '/profitability', label: 'Profitability', icon: TrendingUp },
  { path: '/couriers', label: 'Couriers', icon: Truck },
  { path: '/platform-billing', label: 'Platform Billing', icon: DollarSign },
  { path: '/broadcast', label: 'Broadcast', icon: Send },
  { path: '/notifications', label: 'Notifications', icon: Bell },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export default function MainLayout() {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu when route changes
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="app-layout">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`app-sidebar ${sidebarOpen ? 'open' : 'collapsed'} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <div className="logo-icon">
              <Zap size={24} />
            </div>
            {sidebarOpen && (
              <div className="logo-text">
                <span className="logo-title">Bolt Nexus</span>
                <span className="logo-subtitle">Operations Hub</span>
              </div>
            )}
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <span className="nav-section-label">{sidebarOpen ? 'Main Menu' : ''}</span>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon size={20} />
                {sidebarOpen && (
                  <>
                    <span className="nav-label">{item.label}</span>
                    {item.badge && <span className="nav-badge">{item.badge}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <div className="nav-section">
            <span className="nav-section-label">{sidebarOpen ? 'Resources' : ''}</span>
            <a
              href="/tenant/login"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link tenant-portal-link"
              data-testid="nav-tenant-portal"
            >
              <Truck size={20} />
              {sidebarOpen && (
                <>
                  <span className="nav-label">Tenant Portal</span>
                  <span className="nav-badge demo">Demo</span>
                </>
              )}
            </a>
            <a
              href="/customer/login"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link customer-portal-link"
              data-testid="nav-customer-portal"
            >
              <Users size={20} />
              {sidebarOpen && (
                <>
                  <span className="nav-label">Customer Portal</span>
                  <span className="nav-badge demo">Demo</span>
                </>
              )}
            </a>
            <a
              href="/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link"
              data-testid="nav-docs"
            >
              <BookOpen size={20} />
              {sidebarOpen && (
                <>
                  <span className="nav-label">Documentation</span>
                  <ChevronRight size={16} className="nav-arrow" />
                </>
              )}
            </a>
          </div>
        </nav>

        <div className="sidebar-footer">
          {sidebarOpen && (
            <div className="user-info">
              <div className="user-avatar">KP</div>
              <div className="user-details">
                <span className="user-name">KP Logistics</span>
                <span className="user-role">Administrator</span>
              </div>
            </div>
          )}
          <button className="logout-btn" data-testid="logout-btn">
            <LogOut size={18} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="app-main">
        <header className="app-header">
          <div className="header-left">
            <button 
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={20} />
            </button>
            <h1 className="page-title">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h1>
          </div>
          <div className="header-right">
            <div className="sync-status">
              <span className="sync-indicator active"></span>
              <span className="sync-text">Tasoko Connected</span>
            </div>
            <button className="notification-btn" data-testid="header-notifications">
              <Bell size={20} />
              <span className="notification-badge">3</span>
            </button>
          </div>
        </header>

        <div className="app-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
